/**
* @license Apache-2.0
*
* Copyright (c) 2021 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import { isPrimitive as isNonNegativeInteger } from '@stdlib/assert-is-nonnegative-integer';
import isPositiveInteger from '@stdlib/assert-is-positive-integer';
import { isPrimitive as isInteger } from '@stdlib/assert-is-integer';
import isFunctionArray from '@stdlib/assert-is-function-array';
import isFunction from '@stdlib/assert-is-function';
import isCollection from '@stdlib/assert-is-collection';
import isndarrayLike from '@stdlib/assert-is-ndarray-like';
import format from '@stdlib/error-tools-fmtprodmsg';
import getDType from '@stdlib/ndarray-dtype';
import resolveEnum from '@stdlib/ndarray-base-dtype-resolve-enum';
import dtypes2enums from '@stdlib/ndarray-base-dtypes2enums';
import { ndarray as gindexOfRow } from '@stdlib/blas-ext-base-gindex-of-row';


// MAIN //

/**
* Returns an ndarray function interface which performs multiple dispatch.
*
* @param {(FunctionArray|Function)} fcns - list of ndarray functions
* @param {Collection} types - one-dimensional list of ndarray argument data types
* @param {(Collection|null)} data - ndarray function data (e.g., callbacks)
* @param {PositiveInteger} nargs - total number of ndarray function interface arguments
* @param {NonNegativeInteger} nin - number of input ndarrays
* @param {NonNegativeInteger} nout - number of output ndarrays
* @throws {TypeError} first argument must be either a function or an array of functions
* @throws {TypeError} second argument must be an array-like object
* @throws {TypeError} third argument must be an array-like object or `null`
* @throws {Error} third and first arguments must have the same number of elements
* @throws {TypeError} fourth argument must be a positive integer
* @throws {TypeError} fifth argument must be a nonnegative integer
* @throws {TypeError} sixth argument must be a nonnegative integer
* @throws {Error} fourth argument must equal the specified number of input and output arrays
* @throws {Error} number of types must match the number of functions times the total number of array arguments for each function
* @throws {Error} interface must accept at least one input and/or output ndarray
* @returns {Function} ndarray function interface
*
* @example
* import unary from '@stdlib/ndarray-base-unary';
* import abs from '@stdlib/math-base-special-abs';
* import Float64Array from '@stdlib/array-float64';
* import ndarray from '@stdlib/ndarray-ctor';
*
* var types = [
*     'float64', 'float64'
* ];
*
* var data = [
*     abs
* ];
*
* var absolute = dispatch( unary, types, data, 2, 1, 1 );
*
* // ...
*
* var xbuf = new Float64Array( [ -1.0, -2.0, -3.0, -4.0, -5.0 ] );
* var ybuf = new Float64Array( [ 0.0, 0.0, 0.0, 0.0, 0.0 ] );
*
* var x = ndarray( 'float64', xbuf, [ 5 ], [ 1 ], 0, 'row-major' );
* var y = ndarray( 'float64', ybuf, [ 5 ], [ 1 ], 0, 'row-major' );
*
* absolute( x, y );
* // ybuf => <Float64Array>[ 1.0, 2.0, 3.0, 4.0, 5.0 ]
*/
function dispatch( fcns, types, data, nargs, nin, nout ) {
	var narrays;
	var nfcns;
	var fcn;

	if ( isFunction( fcns ) ) {
		fcn = fcns;
	} else if ( !isFunctionArray( fcns ) ) {
		throw new TypeError( format( '0jr5d', fcns ) );
	}
	if ( !isCollection( types ) ) {
		throw new TypeError( format( '0jr2y', types ) );
	}
	if ( !isCollection( data ) && data !== null ) {
		throw new TypeError( format( '0jr5e', data ) );
	}
	if ( !isPositiveInteger( nargs ) ) {
		throw new TypeError( format( '0jr5f', nargs ) );
	}
	if ( !isNonNegativeInteger( nin ) ) {
		throw new TypeError( format( '0jr5X', nin ) );
	}
	if ( !isNonNegativeInteger( nout ) ) {
		throw new TypeError( format( '0jr5g', nout ) );
	}
	narrays = nin + nout;
	if ( narrays === 0 ) {
		throw new Error( format('0jr0h') );
	}
	if ( nargs !== narrays ) {
		throw new Error( format('0jr0i') );
	}
	if ( fcn ) {
		nfcns = types.length / narrays;
		if ( !isInteger( nfcns ) ) {
			throw new Error( format('0jr0j') );
		}
	} else {
		nfcns = fcns.length;
		if ( types.length !== nfcns*narrays ) {
			throw new Error( format('0jr0j') );
		}
	}
	if ( data && data.length !== nfcns ) {
		throw new Error( format('0jr0k') );
	}
	types = dtypes2enums( types );
	return dispatcher;

	/**
	* ndarray function interface which performs multiple dispatch.
	*
	* @private
	* @param {ndarrayLike} x - ndarray
	* @param {...ndarrayLike} args - ndarray arguments
	* @throws {Error} insufficient arguments
	* @throws {Error} too many arguments
	* @throws {TypeError} input array arguments must be ndarray-like objects
	* @throws {TypeError} output array arguments must be ndarray-like objects
	* @throws {TypeError} unable to resolve an ndarray function supporting the provided ndarray argument data types
	* @returns {(ndarrayLike|Array<ndarrayLike>|void)} destination array(s)
	*/
	function dispatcher() {
		var arrays;
		var dtypes;
		var argc;
		var idx;
		var v;
		var f;
		var i;

		argc = arguments.length;
		if ( argc !== nargs ) {
			if ( argc < nargs ) {
				throw new Error( format('0jr0l') );
			}
			throw new Error( format('0jr0m') );
		}
		arrays = [];
		dtypes = [];
		for ( i = 0; i < nargs; i++ ) {
			v = arguments[ i ];
			if ( !isndarrayLike( v ) ) {
				if ( i < nin ) {
					throw new TypeError( format( '0jr5h', v ) );
				} else {
					throw new TypeError( format( '0jr5i', v ) );
				}
			}
			arrays.push( v );
			dtypes.push( resolveEnum( getDType( v ) ) );
		}
		// Resolve the ndarray function satisfying the input array types:
		idx = gindexOfRow( nfcns, narrays, types, narrays, 1, 0, dtypes, 1, 0 );

		// Check whether we were able to successfully resolve an ndarray function:
		if ( idx < 0 ) {
			throw new TypeError( format('0jr0n') );
		}
		// Retrieve the ndarray function:
		if ( fcn ) {
			f = fcn;
		} else {
			f = fcns[ idx ];
		}
		// Evaluate the ndarray function:
		if ( data ) {
			f( arrays, data[ idx ] );
		} else {
			f( arrays );
		}
		if ( nout === 1 ) {
			return arrays[ narrays-1 ];
		}
		if ( nout === 0 ) {
			return;
		}
		return arrays.slice( nin );
	}
}


// EXPORTS //

export default dispatch;
